/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { C } from "../shared/globals.js";

const fmt$0 = v => new Intl.NumberFormat("en-US", { style:"currency", currency:"USD", minimumFractionDigits:0 }).format(v);

const fmtShares = v => {
  if (v === 0) return "0";
  if (v < 0.001)  return v.toFixed(6);
  if (v < 0.01)   return v.toFixed(5);
  if (v < 0.1)    return v.toFixed(4);
  if (v < 1)      return v.toFixed(3);
  if (v < 10)     return v.toFixed(2);
  if (v < 1000)   return v.toFixed(1);
  return Math.round(v).toLocaleString();
};

const fmtLabel = v => {
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
  if (v >= 1_000)     return (v / 1_000).toFixed(1) + "k";
  if (v >= 1)         return v % 1 === 0 ? String(v) : v.toFixed(2);
  if (v >= 0.01)      return v.toFixed(3);
  return v.toFixed(5);
};

const niceLines = (maxVal, count = 4) => {
  if (maxVal <= 0) return [];
  const rawStep = maxVal / count;
  const mag  = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const step = [1,2,2.5,5,10].map(s => s * mag).find(s => s >= rawStep) || mag * 10;
  const out  = [];
  for (let v = step; v <= maxVal * 1.05; v += step)
    out.push(parseFloat(v.toPrecision(6)));
  return out;
};

const fmtKey = key => {
  const d  = new Date(key);
  const mo = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"][d.getMonth()];
  return String(d.getDate()).padStart(2,"0") + " " + mo;
};

const getKey = d => d.week || d.month;

function MonthlyChart({ data, title }) {
  const [hovered, setHovered] = useState(null);
  const wrapRef = useRef(null);
  const [chartW, setChartW] = useState(600);

  useEffect(() => {
    const obs = new ResizeObserver(es => setChartW(es[0].contentRect.width || 600));
    if (wrapRef.current) obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  if (!data.length) return null;

  const CHART_H = 130;
  const PAD     = { t:8, r:8, b:4, l:42 };
  const gW      = chartW - PAD.l - PAD.r;
  const gH      = CHART_H - PAD.t - PAD.b;
  const maxVal  = Math.max(...data.map(d => d.shares), 0.0001);
  const LINES   = niceLines(maxVal, 4);
  const n       = data.length;
  const barW    = Math.max(4, Math.min(14, Math.floor((gW / n) * 0.5)));
  const slot    = gW / n;

  return (
    <div style={{ background:"var(--bg2)", border:"1px solid var(--border)", borderRadius:10, padding:"14px 16px 10px" }}>
      <div style={{ fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.25em", color:"var(--text4)", marginBottom:10 }}>
        {title || "INVESTED BY WEEK"}
      </div>

      <div ref={wrapRef} style={{ position:"relative" }}>

        {/* SVG — grid lines only, no pointer events */}
        <svg width={chartW} height={CHART_H}
          style={{ display:"block", position:"absolute", top:0, left:0, pointerEvents:"none" }}>
          {LINES.map(l => {
            const y = PAD.t + gH - (l / maxVal) * gH;
            if (y < PAD.t || y > PAD.t + gH) return null;
            return (
              <g key={l}>
                <line x1={PAD.l} y1={y} x2={chartW - PAD.r} y2={y}
                  stroke="var(--border)" strokeWidth="1" strokeDasharray="3 4"/>
                <text x={PAD.l - 4} y={y + 3.5} textAnchor="end"
                  fill="var(--text4)" fontSize="7.5"
                  fontFamily="Montserrat,sans-serif" fontWeight="700">
                  {fmtLabel(l)}
                </text>
              </g>
            );
          })}
        </svg>

        {/* HTML div bars — hover works 100% reliably */}
        <div style={{ position:"relative", height:CHART_H, marginLeft:PAD.l, marginRight:PAD.r }}>
          {data.map((d, i) => {
            const pct   = d.shares / maxVal;
            const barH  = Math.max(pct * gH, 3);
            const isHov = hovered === i;

            return (
              <div key={getKey(d)}
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  position:"absolute",
                  left:  i * slot,
                  width: slot,
                  top:   PAD.t,
                  height: gH,
                  display:"flex",
                  alignItems:"flex-end",
                  justifyContent:"center",
                  cursor:"default",
                }}
              >
                {/* Bar */}
                <div style={{
                  width:  barW,
                  height: barH,
                  borderRadius: barW / 2,
                  background: isHov ? C.orange : `${C.orange}99`,
                  transition:"background 0.15s, transform 0.15s",
                  transform: isHov ? "scaleX(1.4)" : "scaleX(1)",
                  transformOrigin:"center bottom",
                  flexShrink: 0,
                }}/>

                {/* Tooltip above bar */}
                {isHov && (
                  <div style={{
                    position:"absolute",
                    bottom: barH + 10,
                    left:"50%",
                    transform:"translateX(-50%)",
                    zIndex:9999,
                    pointerEvents:"none",
                    whiteSpace:"nowrap",
                  }}>
                    <div style={{
                      background:"var(--bg2)",
                      border:`1px solid ${C.orange}`,
                      borderRadius:8,
                      padding:"10px 14px",
                      fontFamily:"Montserrat,sans-serif",
                      boxShadow:"0 8px 24px rgba(0,0,0,0.6)",
                    }}>
                      <div style={{ fontSize:9, fontWeight:700, letterSpacing:"0.2em", color:"var(--text3)", marginBottom:8 }}>
                        {fmtKey(getKey(d))}
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                        <div>
                          <div style={{ fontSize:8, fontWeight:700, letterSpacing:"0.15em", color:"var(--text4)", marginBottom:2 }}>INVESTED</div>
                          <div style={{ fontSize:15, fontWeight:700, color:C.orange }}>{fmt$0(d.dollars)}</div>
                        </div>
                        <div style={{ width:"100%", height:1, background:"var(--border)" }}/>
                        <div>
                          <div style={{ fontSize:8, fontWeight:700, letterSpacing:"0.15em", color:"var(--text4)", marginBottom:2 }}>SHARES</div>
                          <div style={{ fontSize:14, fontWeight:700, color:"var(--text)" }}>{fmtShares(d.shares)}</div>
                        </div>
                      </div>
                    </div>
                    <div style={{
                      width:0, height:0, margin:"0 auto",
                      borderLeft:"6px solid transparent",
                      borderRight:"6px solid transparent",
                      borderTop:`6px solid ${C.orange}`,
                    }}/>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default MonthlyChart;
